import React, { useCallback, useEffect, useState } from 'react';
import {
  AuditOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  KeyOutlined,
  LockOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SafetyOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Drawer, message, Modal, Select } from 'antd';
import type { GoogleUser } from '../App';
import * as api from '../api/client';
import type {
  CreateSecretPayload,
  SecretAccessAuditRecord,
  SecretCategory,
  SecretRiskLevel,
  UpdateSecretPayload,
  UserSecretSummary,
} from '../api/client';

// ── Constants ─────────────────────────────────────────────────────────────────

const RISK_LEVELS: SecretRiskLevel[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CATEGORIES: SecretCategory[] = [
  'API_KEY',
  'PASSWORD',
  'SSH',
  'DATABASE',
  'CLOUD',
  'TOKEN',
  'CERTIFICATE',
  'OTHER',
];

const RISK_COLORS: Record<SecretRiskLevel, string> = {
  LOW: '#52c41a',
  MEDIUM: '#faad14',
  HIGH: '#fa8c16',
  CRITICAL: '#f5222d',
};

const CATEGORY_ICONS: Record<SecretCategory, React.ReactNode> = {
  API_KEY: <KeyOutlined />,
  PASSWORD: <LockOutlined />,
  SSH: <SafetyOutlined />,
  DATABASE: <SafetyOutlined />,
  CLOUD: <KeyOutlined />,
  TOKEN: <KeyOutlined />,
  CERTIFICATE: <SafetyOutlined />,
  OTHER: <KeyOutlined />,
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface SecretFormValues {
  secret_name: string;
  secret_value: string;
  description: string;
  category: SecretCategory;
  risk_level: SecretRiskLevel;
  requires_approval: boolean;
  tags: string[];
  environment: string;
  expires_on: string;
  rotation_interval_days: number;
}

const DEFAULT_FORM: SecretFormValues = {
  secret_name: '',
  secret_value: '',
  description: '',
  category: 'OTHER',
  risk_level: 'LOW',
  requires_approval: false,
  tags: [],
  environment: '',
  expires_on: '',
  rotation_interval_days: 0,
};

interface Props {
  user: GoogleUser | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function riskBadge(level: SecretRiskLevel) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        background: RISK_COLORS[level] + '22',
        color: RISK_COLORS[level],
        border: `1px solid ${RISK_COLORS[level]}44`,
        letterSpacing: 0.5,
      }}
    >
      {level}
    </span>
  );
}

function categoryBadge(cat: SecretCategory) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 7px',
        borderRadius: 4,
        fontSize: 10,
        background: 'rgba(255,255,255,0.06)',
        color: '#d1d5db',
        border: '1px solid #333',
      }}
    >
      {CATEGORY_ICONS[cat]}
      {cat.replace(/_/g, ' ')}
    </span>
  );
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function normalizeTags(tags: string[]) {
  return Array.from(new Set(tags.map(tag => tag.trim()).filter(Boolean)));
}

// ── Create/Edit drawer ────────────────────────────────────────────────────────

interface SecretFormProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: SecretFormValues;
  saving: boolean;
  onCancel: () => void;
  onSave: (values: SecretFormValues) => void;
}

const SecretFormDrawer: React.FC<SecretFormProps> = ({
  open, mode, initialValues, saving, onCancel, onSave,
}) => {
  const [form, setForm] = useState<SecretFormValues>(initialValues);
  const [showValue, setShowValue] = useState(false);

  useEffect(() => {
    if (open) { setForm(initialValues); setShowValue(false); }
  }, [open, initialValues]);

  const set = (key: keyof SecretFormValues, value: any) =>
    setForm(f => ({ ...f, [key]: value }));

  const handleSave = () => {
    if (!form.secret_name.trim()) { message.error('Secret name is required'); return; }
    if (mode === 'create' && !form.secret_value.trim()) { message.error('Secret value is required'); return; }
    onSave(form);
  };

  const isHighRisk = form.risk_level === 'HIGH' || form.risk_level === 'CRITICAL';

  return (
    <Drawer
      className="praxys-secrets-drawer"
      title={(
        <span className="praxys-secrets-drawer-title">
          <LockOutlined />
          {mode === 'create' ? 'Add Secret' : 'Edit Secret'}
        </span>
      )}
      placement="right"
      width={460}
      open={open}
      onClose={onCancel}
      closable
      closeIcon={<span aria-hidden>✕</span>}
      bodyStyle={{ padding: 0 }}
    >
      <div className="praxys-secrets-drawer-content">
        <div className="praxys-secrets-drawer-body">
          <p className="praxys-secrets-drawer-subtitle">
            {mode === 'create'
              ? 'Encrypted at rest — never returned after creation.'
              : 'Update metadata or rotate the value. Leave value blank to keep current.'}
          </p>
          {/* Name */}
          <div style={{ marginBottom: 14 }}>
            <label className="praxys-secrets-form-label">
              Secret Name <span style={{ color: '#f5222d' }}>*</span>
            </label>
            <input
              className={`praxys-secrets-form-control ${mode === 'edit' ? 'praxys-secrets-form-control--readonly' : ''}`}
              type="text"
              value={form.secret_name}
              onChange={e => set('secret_name', e.target.value.toUpperCase().replace(/\s/g, '_'))}
              placeholder="e.g. OPENAI_API_KEY"
              disabled={mode === 'edit'}
              style={{ fontFamily: 'monospace' }}
            />
            <span className="praxys-secrets-form-hint">Uppercase. Reference with @SECRET_NAME in tasks.</span>
          </div>

          {/* Value */}
          <div style={{ marginBottom: 14 }}>
            <label className="praxys-secrets-form-label">
              Secret Value {mode === 'create' && <span style={{ color: '#f5222d' }}>*</span>}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                className="praxys-secrets-form-control"
                type={showValue ? 'text' : 'password'}
                value={form.secret_value}
                onChange={e => set('secret_value', e.target.value)}
                placeholder={mode === 'edit' ? 'Leave blank to keep current' : 'Enter secret value…'}
                autoComplete="new-password"
                style={{ paddingRight: 34, fontFamily: 'monospace' }}
              />
              <button
                type="button"
                onClick={() => setShowValue(v => !v)}
                className="praxys-secrets-toggle-btn"
              >
                <EyeInvisibleOutlined />
              </button>
            </div>
            <span className="praxys-secrets-form-hint">AES-128 encrypted. Never stored or returned in plaintext.</span>
          </div>

          {/* Category + Risk */}
          <div className="praxys-secrets-form-grid" style={{ marginBottom: 14 }}>
            <div>
              <label className="praxys-secrets-form-label">Category</label>
              <select
                className="praxys-secrets-form-control"
                value={form.category}
                onChange={e => set('category', e.target.value)}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="praxys-secrets-form-label">Risk Level</label>
              <select
                className="praxys-secrets-form-control"
                value={form.risk_level}
                onChange={e => set('risk_level', e.target.value as SecretRiskLevel)}
                style={{
                  border: `1px solid ${RISK_COLORS[form.risk_level]}`,
                  color: RISK_COLORS[form.risk_level], fontWeight: 600,
                }}
              >
                {RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 14 }}>
            <label className="praxys-secrets-form-label">Description</label>
            <textarea
              className="praxys-secrets-form-control praxys-secrets-form-control--textarea"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="What is this secret used for?"
              rows={2}
            />
          </div>

          {/* Approval */}
          <div className={`praxys-secrets-approval-card ${isHighRisk ? 'praxys-secrets-approval-card--high-risk' : ''}`}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.requires_approval || isHighRisk}
                onChange={e => set('requires_approval', e.target.checked)}
                disabled={isHighRisk}
                style={{ accentColor: '#10a37f' }}
              />
              <span className="praxys-secrets-approval-title">Require human approval before use</span>
            </label>
            {isHighRisk && (
              <p className="praxys-secrets-approval-note">
                <WarningOutlined /> Automatically enforced for HIGH/CRITICAL risk.
              </p>
            )}
          </div>

          {/* Tags + Env */}
          <div className="praxys-secrets-form-grid" style={{ marginBottom: 14 }}>
            <div>
              <label className="praxys-secrets-form-label">Tags</label>
              <Select
                mode="tags"
                className="praxys-secrets-tags-select"
                value={form.tags}
                onChange={value => set('tags', normalizeTags(value))}
                placeholder="prod, openai"
                tokenSeparators={[',']}
              />
              <span className="praxys-secrets-form-hint">Press Enter or comma to create a tag</span>
            </div>
            <div>
              <label className="praxys-secrets-form-label">Environment</label>
              <input
                className="praxys-secrets-form-control"
                type="text" value={form.environment} onChange={e => set('environment', e.target.value)}
                placeholder="production"
              />
            </div>
          </div>

          {/* Expiry + Rotation */}
          <div className="praxys-secrets-form-grid">
            <div>
              <label className="praxys-secrets-form-label">Expires On</label>
              <input
                className="praxys-secrets-form-control"
                type="date" value={form.expires_on} onChange={e => set('expires_on', e.target.value)}
              />
            </div>
            <div>
              <label className="praxys-secrets-form-label">Rotate Every (days)</label>
              <input
                className="praxys-secrets-form-control"
                type="number" min={0} value={form.rotation_interval_days}
                onChange={e => set('rotation_interval_days', parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="praxys-secrets-drawer-footer">
          <button onClick={onCancel} className="praxys-secrets-drawer-btn praxys-secrets-drawer-btn--secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="praxys-secrets-drawer-btn praxys-secrets-drawer-btn--primary"
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Save Secret' : 'Update Secret'}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

// ── Audit modal ───────────────────────────────────────────────────────────────

interface AuditModalProps {
  open: boolean;
  secretName: string;
  records: SecretAccessAuditRecord[];
  loading: boolean;
  onClose: () => void;
}

const AuditModal: React.FC<AuditModalProps> = ({ open, secretName, records, loading, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    title={<span><AuditOutlined style={{ marginRight: 8, color: '#6366f1' }} />Audit — {secretName}</span>}
    width={640}
  >
    {loading ? (
      <div style={{ textAlign: 'center', padding: 28, color: '#aaa' }}>Loading…</div>
    ) : records.length === 0 ? (
      <div style={{ textAlign: 'center', padding: 28, color: '#aaa' }}>No audit records yet.</div>
    ) : (
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {records.map(r => (
          <div key={r.id} style={{ padding: '8px 12px', borderBottom: '1px solid #f5f5f5', display: 'grid', gridTemplateColumns: '140px 1fr', gap: 6, fontSize: 12 }}>
            <span style={{ color: '#888', fontFamily: 'monospace' }}>{new Date(r.created_on).toLocaleString()}</span>
            <span>
              <strong style={{ fontFamily: 'monospace', color: '#444' }}>{r.access_type}</strong>
              {r.task_id && <span style={{ marginLeft: 8, color: '#999' }}>task:{r.task_id.slice(0, 8)}</span>}
              {r.tool_name && <span style={{ marginLeft: 8, color: '#999' }}>tool:{r.tool_name}</span>}
              {r.approval_status && r.approval_status !== '' && (
                <span style={{
                  marginLeft: 8, padding: '1px 5px', borderRadius: 3, fontSize: 10,
                  background: r.approval_status === 'APPROVED' ? '#f6ffed' : r.approval_status === 'REJECTED' ? '#fff2f0' : '#fffbe6',
                  color: r.approval_status === 'APPROVED' ? '#52c41a' : r.approval_status === 'REJECTED' ? '#f5222d' : '#faad14',
                  border: '1px solid currentColor',
                }}>
                  {r.approval_status}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    )}
  </Modal>
);

// ── Main component ────────────────────────────────────────────────────────────

const SecretsManagerView: React.FC<Props> = ({ user }) => {
  const userId = user?.email || '';

  const [secrets, setSecrets] = useState<UserSecretSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [editingSecret, setEditingSecret] = useState<UserSecretSummary | null>(null);
  const [formValues, setFormValues] = useState<SecretFormValues>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditSecret, setAuditSecret] = useState<UserSecretSummary | null>(null);
  const [auditRecords, setAuditRecords] = useState<SecretAccessAuditRecord[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [busyId, setBusyId] = useState<string | null>(null);

  const loadSecrets = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.listUserSecrets(userId, {
        search: search || undefined,
        category: filterCategory || undefined,
        risk_level: filterRisk || undefined,
        is_active: filterActive === '' ? undefined : filterActive === 'true',
      });
      setSecrets(res.data.secrets || []);
    } catch {
      message.error('Failed to load secrets');
    } finally {
      setLoading(false);
    }
  }, [userId, search, filterCategory, filterRisk, filterActive]);

  useEffect(() => { loadSecrets(); }, [loadSecrets]);

  const openCreate = () => {
    setDrawerMode('create'); setEditingSecret(null);
    setFormValues(DEFAULT_FORM); setDrawerOpen(true);
  };

  const openEdit = (s: UserSecretSummary) => {
    setDrawerMode('edit'); setEditingSecret(s);
    setFormValues({
      secret_name: s.secret_name, secret_value: '',
      description: s.description, category: s.category,
      risk_level: s.risk_level, requires_approval: s.requires_approval,
      tags: s.tags || [], environment: s.environment,
      expires_on: s.expires_on ? new Date(s.expires_on).toISOString().split('T')[0] : '',
      rotation_interval_days: s.rotation_interval_days,
    });
    setDrawerOpen(true);
  };

  const handleSave = async (values: SecretFormValues) => {
    setSaving(true);
    const tags = normalizeTags(values.tags);
    try {
      if (drawerMode === 'create') {
        const payload: CreateSecretPayload = {
          user_id: userId, secret_name: values.secret_name, secret_value: values.secret_value,
          description: values.description, category: values.category, risk_level: values.risk_level,
          requires_approval: values.requires_approval, tags, environment: values.environment,
          expires_on: values.expires_on || null, rotation_interval_days: values.rotation_interval_days,
        };
        await api.createUserSecret(payload);
        message.success(`Secret '${values.secret_name}' created`);
      } else if (editingSecret) {
        const payload: UpdateSecretPayload = {
          user_id: userId, description: values.description, category: values.category,
          risk_level: values.risk_level, requires_approval: values.requires_approval,
          tags, environment: values.environment,
          expires_on: values.expires_on || null, rotation_interval_days: values.rotation_interval_days,
        };
        if (values.secret_value.trim()) payload.secret_value = values.secret_value;
        await api.updateUserSecret(editingSecret.id, payload);
        message.success(`Secret '${editingSecret.secret_name}' updated`);
      }
      setDrawerOpen(false);
      loadSecrets();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (s: UserSecretSummary) => {
    setBusyId(s.id);
    try {
      await api.updateUserSecret(s.id, { user_id: userId, is_active: !s.is_active });
      message.success(`'${s.secret_name}' ${s.is_active ? 'disabled' : 'enabled'}`);
      loadSecrets();
    } catch { message.error('Update failed'); }
    finally { setBusyId(null); }
  };

  const handleDelete = (s: UserSecretSummary) => {
    Modal.confirm({
      title: `Delete '${s.secret_name}'?`,
      content: 'Permanent. Tasks referencing this secret will fail to resolve it.',
      okText: 'Delete', okType: 'danger',
      onOk: async () => {
        try {
          await api.deleteUserSecret(s.id, userId);
          message.success(`'${s.secret_name}' deleted`);
          loadSecrets();
        } catch { message.error('Delete failed'); }
      },
    });
  };

  const openAudit = async (s: UserSecretSummary) => {
    setAuditSecret(s); setAuditRecords([]); setAuditOpen(true); setAuditLoading(true);
    try {
      const res = await api.getSecretAuditHistory(s.id, userId);
      setAuditRecords(res.data.audit || []);
    } catch { message.error('Failed to load audit history'); }
    finally { setAuditLoading(false); }
  };

  const totalActive = secrets.filter(s => s.is_active).length;
  const totalHighRisk = secrets.filter(s => s.risk_level === 'HIGH' || s.risk_level === 'CRITICAL').length;

  // ── Render as a profile section card ──────────────────────────────────────

  return (
    <section className="praxys-apikey-card">
      {/* Header row */}
      <div className="praxys-apikey-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LockOutlined style={{ color: '#6366f1' }} />
          <h3 className="praxys-apikey-card-title" style={{ margin: 0 }}>Secrets Manager</h3>
          {secrets.length > 0 && (
            <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>
              {totalActive} active
              {totalHighRisk > 0 && (
                <span style={{ marginLeft: 6, color: '#f5222d' }}>· {totalHighRisk} high risk</span>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          className="praxys-apikey-add-btn"
          onClick={openCreate}
          title="Add secret"
          aria-label="Add secret"
        >
          <PlusOutlined />
        </button>
      </div>

      {/* Sub-description */}
      <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888', lineHeight: 1.5 }}>
        Securely store credentials, API keys, and tokens for use in tasks and tools.
        Reference secrets in any prompt with <code className="praxys-secrets-inline-code">@SECRET_NAME</code>.
      </p>

      {/* Filter bar */}
      <div className="praxys-secrets-filter-bar">
        <div className="praxys-secrets-search">
          <SearchOutlined className="praxys-secrets-search-icon" />
          <input
            className="praxys-secrets-filter-control"
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
          />
        </div>
        {[
          { value: filterCategory, setter: setFilterCategory, placeholder: 'All Categories', options: CATEGORIES.map(c => ({ value: c, label: c.replace(/_/g, ' ') })) },
          { value: filterRisk, setter: setFilterRisk, placeholder: 'All Risk Levels', options: RISK_LEVELS.map(r => ({ value: r, label: r })) },
          { value: filterActive, setter: (v: any) => setFilterActive(v), placeholder: 'All Status', options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Disabled' }] },
        ].map((f, i) => (
          <select
            key={i}
            className="praxys-secrets-filter-control"
            value={f.value}
            onChange={e => f.setter(e.target.value)}
          >
            <option value="">{f.placeholder}</option>
            {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="praxys-view-empty">Loading secrets…</div>
      ) : secrets.length === 0 ? (
        <div className="praxys-view-empty">
          No secrets yet.{' '}
          <button type="button" className="praxys-apikey-inline-link" onClick={openCreate}>
            Add your first secret
          </button>{' '}
          to get started.
        </div>
      ) : (
        <div className="praxys-apikey-table-wrapper">
          <table className="praxys-apikey-table praxys-secrets-table" aria-label="Secrets Manager">
            <thead>
              <tr className="praxys-apikey-thead-row">
                {['Secret Name', 'Category', 'Risk', 'Last Used', 'Uses', 'Approval', 'Status', ''].map(h => (
                  <th key={h} className={`praxys-apikey-th${h === '' ? ' praxys-apikey-th--options' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {secrets.map(s => (
                <tr key={s.id} className={`praxys-apikey-row${s.is_active ? '' : ' praxys-apikey-row--inactive'}`}>
                  {/* Name */}
                  <td className="praxys-apikey-cell praxys-secrets-cell praxys-secrets-cell--name">
                    <div className="praxys-secrets-name">@{s.secret_name}</div>
                    {s.description && <div className="praxys-secrets-description">{s.description}</div>}
                  </td>
                  <td className="praxys-apikey-cell praxys-secrets-cell">{categoryBadge(s.category)}</td>
                  <td className="praxys-apikey-cell praxys-secrets-cell">{riskBadge(s.risk_level)}</td>
                  <td className="praxys-apikey-cell praxys-secrets-cell praxys-secrets-cell--muted">{formatDate(s.last_used_on)}</td>
                  <td className="praxys-apikey-cell praxys-secrets-cell praxys-secrets-cell--center">{s.usage_count}</td>
                  <td className="praxys-apikey-cell praxys-secrets-cell">
                    {s.requires_approval
                      ? <span className="praxys-secrets-approval-pill"><SafetyOutlined /> Required</span>
                      : <span className="praxys-secrets-muted-dash">—</span>}
                  </td>
                  <td className="praxys-apikey-cell praxys-secrets-cell">
                    <span className={`praxys-secrets-status-pill ${s.is_active ? 'praxys-secrets-status-pill--active' : 'praxys-secrets-status-pill--disabled'}`}>
                      {s.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  {/* Actions */}
                  <td className="praxys-apikey-cell praxys-apikey-cell--options">
                    <div className="praxys-apikey-options">
                      <button type="button" title="Edit" onClick={() => openEdit(s)} className="praxys-apikey-icon-btn" style={{ color: '#818cf8' }}><EditOutlined /></button>
                      <button type="button" title={s.is_active ? 'Disable' : 'Enable'} onClick={() => handleToggleActive(s)} disabled={busyId === s.id} className={`praxys-apikey-icon-btn${!s.is_active ? ' praxys-apikey-icon-btn--success' : ''}`} style={s.is_active ? { color: '#f59e0b' } : undefined}>
                        {s.is_active ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                      </button>
                      <button type="button" title="Audit" onClick={() => openAudit(s)} className="praxys-apikey-icon-btn"><AuditOutlined /></button>
                      <button type="button" title="Delete" onClick={() => handleDelete(s)} className="praxys-apikey-icon-btn praxys-apikey-icon-btn--danger" style={{ color: '#f87171' }}><DeleteOutlined /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawers / Modals */}
      <SecretFormDrawer
        open={drawerOpen} mode={drawerMode} initialValues={formValues}
        saving={saving} onCancel={() => setDrawerOpen(false)} onSave={handleSave}
      />
      <AuditModal
        open={auditOpen} secretName={auditSecret?.secret_name || ''}
        records={auditRecords} loading={auditLoading} onClose={() => setAuditOpen(false)}
      />
    </section>
  );
};

export default SecretsManagerView;
